// app/javascript/controllers/order_form_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "quantityInput",
    "totalDisplay",
    "stockWarning",
    "stockWarningText",
    "deliveryAddressSection",
    "pickupInfoSection",
    "deliveryAddress",
    "deliveryPhone",
    "deliveryDateInput",
    "submitButton",
    // Fee breakdown targets
    "simpleTotalSection",
    "feeBreakdownSection",
    "subtotalDisplay",
    "platformFeeDisplay",
    "buyerTotalDisplay",
    "sellerReceivesDisplay"
  , "mathDisplay",
    "approxTotalDisplay", "approxBuyerTotalDisplay"
  ]

  static values = {
    region: { type: String, default: "greece" }, // the product's region
    pricePerUnit: Number,
    priceTiers: { type: Array, default: [] },   // sale-priced, sorted by min_qty asc
    unitLabel: { type: String, default: "" },
    minOrder: { type: Number, default: 0 },
    minOrderMessage: { type: String, default: "" },
    availableStock: Number,
    productUnit: String,
    currencySymbol: { type: String, default: "€" },
    // Viewer's display currency (docs/CURRENCY_ARCHITECTURE.md): when it differs
    // from the listing currency the server passes the rate and the totals get
    // an "≈" approximation next to them. No rate = no approximation.
    displayCurrency: { type: String, default: "" },
    displaySymbol: { type: String, default: "" },
    displayDecimals: { type: Number, default: 2 },
    displayRate: { type: Number, default: 0 },
    // Per-seller/region platform fee % (5%/3.5%/3%/0%). Passed from the view so
    // the live preview matches what Order#calculated_platform_fee will charge.
    platformFeePercent: { type: Number, default: 5.0 },
    // Thailand: the buyer pays the fee on every payment method, not only card
    // (Order#platform_fee_applies?), so the breakdown shows for all of them.
    feeOnEveryMethod: { type: Boolean, default: false },
    translations: Object
  }

  // Transparency model: buyer pays platform fee only, seller absorbs Stripe fee

  connect() {
    this.updateTotal()
    this.checkFulfillmentMethod()
    this.checkPaymentMethod()
    this.setupValidation()
  }

  setupValidation() {
    // Disable browser validation - we handle it with JS
    const form = this.element.closest('form') || this.element.querySelector('form')
    if (form) {
      form.setAttribute('novalidate', 'novalidate')
      form.addEventListener('submit', (e) => this.validateOnSubmit(e))
    }
  }

  // ==========================================
  // SUBMIT VALIDATION
  // ==========================================

  validateOnSubmit(event) {
    const errors = this.validateAllFields()

    if (errors.length > 0) {
      event.preventDefault()
      // Focus first error field
      const firstErrorField = this.element.querySelector('.border-red-500, .border-red-600')
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        firstErrorField.focus()
      }
      return false
    }
    return true
  }

  validateAllFields() {
    const errors = []

    // Validate quantity
    if (this.hasQuantityInputTarget) {
      const quantityError = this.validateQuantity()
      if (quantityError) errors.push(quantityError)
    }

    // Validate delivery date
    if (this.hasDeliveryDateInputTarget) {
      const dateError = this.validateDeliveryDate()
      if (dateError) errors.push(dateError)
    }

    // Validate fulfillment method selected
    const fulfillmentError = this.validateFulfillmentMethod()
    if (fulfillmentError) errors.push(fulfillmentError)

    // Validate payment method selected
    const paymentError = this.validatePaymentMethod()
    if (paymentError) errors.push(paymentError)

    // Validate delivery address only if delivery selected
    if (this.isDeliverySelected() && this.hasDeliveryAddressTarget) {
      const addressError = this.validateDeliveryAddress()
      if (addressError) errors.push(addressError)
    }

    // Phone is always required (both delivery and pickup)
    if (this.hasDeliveryPhoneTarget) {
      const phoneError = this.validateDeliveryPhone()
      if (phoneError) errors.push(phoneError)
    }

    return errors
  }

  // ==========================================
  // INDIVIDUAL FIELD VALIDATORS (onblur)
  // ==========================================

  validateQuantityOnBlur(event) {
    this.validateQuantity()
    this.updateTotal()
    this.checkStock()
  }

  validateQuantity() {
    if (!this.hasQuantityInputTarget) return null

    const field = this.quantityInputTarget
    const value = field.value.trim()
    const t = this.translationsValue?.order?.quantity || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Quantity is required')
    }

    const num = parseFloat(value)
    if (isNaN(num)) {
      return this.showFieldError(field, t.invalid || 'Please enter a valid number')
    }

    if (num <= 0) {
      return this.showFieldError(field, t.min || 'Quantity must be greater than 0')
    }

    if (this.minOrderValue > 0 && num < this.minOrderValue) {
      return this.showFieldError(field, this.minOrderMessageValue || t.min)
    }

    this.showFieldSuccess(field)
    return null
  }

  validateDeliveryDateOnBlur(event) {
    this.validateDeliveryDate()
  }

  validateDeliveryDate() {
    if (!this.hasDeliveryDateInputTarget) return null

    const field = this.deliveryDateInputTarget
    const value = field.value
    const t = this.translationsValue?.order?.requested_delivery_date || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Please select a delivery date')
    }

    const selectedDate = new Date(value)
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)

    if (selectedDate < tomorrow) {
      return this.showFieldError(field, t.min_date || 'Delivery date must be tomorrow or later')
    }

    this.showFieldSuccess(field)
    return null
  }

  validateFulfillmentMethod() {
    const selected = document.querySelector('input[name="order[fulfillment_method]"]:checked')
    const t = this.translationsValue?.order?.fulfillment_method || {}

    if (!selected) {
      // Highlight the fulfillment section
      return t.required || 'Please select delivery or pickup'
    }
    return null
  }

  validatePaymentMethod() {
    const selected = document.querySelector('input[name="order[payment_method]"]:checked')
    const t = this.translationsValue?.order?.payment_method || {}

    if (!selected) {
      return t.required || 'Please select a payment method'
    }
    return null
  }

  validateDeliveryAddressOnBlur(event) {
    if (this.isDeliverySelected()) {
      this.validateDeliveryAddress()
    }
  }

  validateDeliveryAddress() {
    if (!this.hasDeliveryAddressTarget) return null

    const field = this.deliveryAddressTarget
    const value = field.value.trim()
    const t = this.translationsValue?.order?.delivery_address || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Delivery address is required')
    }

    if (value.length < 10) {
      return this.showFieldError(field, t.too_short || 'Please provide a complete address (at least 10 characters)')
    }

    this.showFieldSuccess(field)
    return null
  }

  validateDeliveryPhoneOnBlur(event) {
    if (this.isDeliverySelected()) {
      this.validateDeliveryPhone()
    }
  }

  validateDeliveryPhone() {
    if (!this.hasDeliveryPhoneTarget) return null

    const field = this.deliveryPhoneTarget
    const value = field.value.trim()
    const t = this.translationsValue?.order?.delivery_phone || {}

    if (!value) {
      return this.showFieldError(field, t.required || 'Contact phone is required')
    }

    // Same rule as the server (Order#valid_phone_number_for_region), chosen by
    // the PRODUCT's region. Until 2026-09-27 every buyer got the Greek check,
    // so Thai buyers could not send an order with their own phone number.
    if (this.regionValue === 'greece') {
      if (!this.isValidGreekPhone(value)) {
        return this.showFieldError(field, t.invalid_format || 'Please enter a valid Greek phone number')
      }
    } else if (!this.isValidPhoneLength(value, this.regionValue === 'thailand' ? 9 : 7, 15)) {
      const message = this.regionValue === 'thailand' ? t.invalid_format_thailand : t.invalid_format_other
      return this.showFieldError(field, message || 'Please enter a valid phone number')
    }

    this.showFieldSuccess(field)
    return null
  }

  isValidPhoneLength(value, min, max) {
    if (!/^[\d\s+\-()]+$/.test(value)) return false
    const digits = value.replace(/\D/g, '').length
    return digits >= min && digits <= max
  }

  isValidGreekPhone(value) {
    const digitsOnly = value.replace(/\D/g, '')
    const validFormats = [
      /^6\d{9}$/,        // Mobile: 6912345678 (10 digits)
      /^306\d{9}$/,      // Mobile with country code: 306912345678 (12 digits)
      /^2\d{9}$/,        // Landline: 2810123456 (10 digits)
      /^302\d{9}$/       // Landline with country code: 302810123456 (12 digits)
    ]
    return validFormats.some(pattern => pattern.test(digitsOnly))
  }

  // ==========================================
  // UI HELPER METHODS
  // ==========================================

  showFieldError(field, message) {
    // Remove any existing state
    field.classList.remove('border-green-500', 'border-green-600', 'border-gray-300')
    field.classList.add('border-red-500')

    // Find or create error element
    const errorId = `${field.id || field.name}-error`
    let errorEl = document.getElementById(errorId)

    if (!errorEl) {
      errorEl = document.createElement('p')
      errorEl.id = errorId
      errorEl.className = 'text-sm text-red-600 mt-1.5 flex items-center gap-1 validation-error'
      errorEl.innerHTML = `<i class="fas fa-exclamation-circle"></i><span></span>`

      // Insert after the field or its container
      const container = field.closest('.relative') || field.parentElement
      container.appendChild(errorEl)
    }

    errorEl.querySelector('span').textContent = message
    errorEl.classList.remove('hidden')

    // Hide any help text
    const helpText = field.parentElement.querySelector('.text-accent-green:not(.validation-error)')
    if (helpText) helpText.classList.add('hidden')

    return message
  }

  showFieldSuccess(field) {
    // Remove error state
    field.classList.remove('border-red-500', 'border-red-600', 'border-gray-300')
    field.classList.add('border-green-500')

    // Hide error message
    const errorId = `${field.id || field.name}-error`
    const errorEl = document.getElementById(errorId)
    if (errorEl) errorEl.classList.add('hidden')

    // Show help text again
    const helpText = field.parentElement.querySelector('.text-accent-green:not(.validation-error)')
    if (helpText) helpText.classList.remove('hidden')
  }

  clearFieldValidation(field) {
    field.classList.remove('border-red-500', 'border-red-600', 'border-green-500', 'border-green-600')
    field.classList.add('border-gray-300')

    const errorId = `${field.id || field.name}-error`
    const errorEl = document.getElementById(errorId)
    if (errorEl) errorEl.remove()

    const helpText = field.parentElement.querySelector('.text-accent-green:not(.validation-error)')
    if (helpText) helpText.classList.remove('hidden')
  }

  // ==========================================
  // FULFILLMENT & TOTAL CALCULATIONS
  // ==========================================

  isDeliverySelected() {
    const deliveryRadio = document.querySelector('input[name="order[fulfillment_method]"][value="delivery"]')
    return deliveryRadio && deliveryRadio.checked
  }

  updateQuantity(event) {
    this.updateTotal()
    this.checkStock()
    this.validateQuantity()
    // Also update fee breakdown if it is showing
    if (this.feeApplies()) {
      this.updateFeeBreakdown()
    }
  }

  checkFulfillmentMethod(event) {
    const isDelivery = this.isDeliverySelected()

    // Show/hide delivery address section (address field only)
    if (this.hasDeliveryAddressSectionTarget) {
      if (isDelivery) {
        this.deliveryAddressSectionTarget.classList.remove('hidden')
        if (this.hasDeliveryAddressTarget) this.deliveryAddressTarget.disabled = false
      } else {
        this.deliveryAddressSectionTarget.classList.add('hidden')
        if (this.hasDeliveryAddressTarget) {
          this.deliveryAddressTarget.disabled = true
          this.clearFieldValidation(this.deliveryAddressTarget)
        }
      }
    }

    // Show/hide pickup info section
    if (this.hasPickupInfoSectionTarget) {
      if (isDelivery) {
        this.pickupInfoSectionTarget.classList.add('hidden')
      } else {
        this.pickupInfoSectionTarget.classList.remove('hidden')
      }
    }
  }

  // Unit price for a quantity: best applicable volume tier (tiers arrive
  // sale-priced + sorted asc) or the base sale price. Mirrors
  // Product#sale_price_for - the server recomputes authoritatively.
  unitPriceFor(quantity) {
    let price = this.pricePerUnitValue
    for (const tier of this.priceTiersValue) {
      if (quantity >= tier.min_qty) price = tier.price
    }
    return price
  }

  updateTotal() {
    if (!this.hasQuantityInputTarget || !this.hasTotalDisplayTarget) return

    const quantity = parseFloat(this.quantityInputTarget.value) || 0
    const unitPrice = this.unitPriceFor(quantity)
    const total = unitPrice * quantity

    // Viewer's currency as the total when a rate is known, the listing
    // currency in brackets after it; otherwise the listing currency alone.
    if (this.displayRateValue) {
      this.totalDisplayTarget.textContent = this.formatApprox(total)
      if (this.hasApproxTotalDisplayTarget) this.approxTotalDisplayTarget.textContent = "(" + this.currencySymbolValue + total.toFixed(2) + ")"
    } else {
      this.totalDisplayTarget.textContent = this.currencySymbolValue + total.toFixed(2)
      if (this.hasApproxTotalDisplayTarget) this.approxTotalDisplayTarget.textContent = ""
    }
    // Let payment options with an amount limit (IRIS) react to the new total.
    this.dispatch("total", { detail: { total } })

    // "20 kg x B252.00" - make the math visible, especially which volume
    // tier actually won for this quantity.
    if (this.hasMathDisplayTarget) {
      this.mathDisplayTarget.textContent = quantity > 0
        ? `${quantity} ${this.unitLabelValue} × ${this.currencySymbolValue}${unitPrice.toFixed(2)}`
        : ""
    }
  }

  checkStock() {
    if (!this.hasStockWarningTarget || !this.hasStockWarningTextTarget) return
    if (!this.hasAvailableStockValue || !this.availableStockValue) return

    const quantity = parseFloat(this.quantityInputTarget.value) || 0

    if (quantity > this.availableStockValue) {
      const requestedText = `${quantity} ${this.productUnitValue}`
      const availableText = `${this.availableStockValue} ${this.productUnitValue}`

      const warningMessage = this.stockWarningTextTarget.dataset.template
        .replace('REQUESTED_PLACEHOLDER', requestedText)
        .replace('AVAILABLE_PLACEHOLDER', availableText)

      this.stockWarningTextTarget.textContent = warningMessage
      this.stockWarningTarget.classList.remove('hidden')
    } else {
      this.stockWarningTarget.classList.add('hidden')
    }
  }

  // ==========================================
  // PAYMENT METHOD & FEE CALCULATIONS
  // ==========================================

  isCardSelected() {
    const cardRadio = document.querySelector('input[name="order[payment_method]"][value="card"]')
    return cardRadio && cardRadio.checked
  }

  feeApplies() {
    return this.feeOnEveryMethodValue || this.isCardSelected()
  }

  checkPaymentMethod(event) {
    // Show/hide fee breakdown vs simple total
    if (this.hasSimpleTotalSectionTarget && this.hasFeeBreakdownSectionTarget) {
      if (this.feeApplies()) {
        this.simpleTotalSectionTarget.classList.add('hidden')
        this.feeBreakdownSectionTarget.classList.remove('hidden')
        this.updateFeeBreakdown()
      } else {
        this.simpleTotalSectionTarget.classList.remove('hidden')
        this.feeBreakdownSectionTarget.classList.add('hidden')
      }
    }
  }

  updateFeeBreakdown() {
    if (!this.hasQuantityInputTarget) return

    const quantity = parseFloat(this.quantityInputTarget.value) || 0
    const subtotal = this.unitPriceFor(quantity) * quantity

    // Calculate fees (matching Order model logic)
    // Transparency model: buyer pays platform fee only (per-seller/region rate)
    // Seller absorbs Stripe fee (~1.5-3%)
    const platformFee = this.roundTo2(subtotal * this.platformFeePercentValue / 100)
    const buyerTotal = this.roundTo2(subtotal + platformFee)

    // Update displays
    // One convention for the whole breakdown: the viewer's currency when a
    // rate is known (listing currency in brackets on the total), else listing.
    if (this.hasSubtotalDisplayTarget) {
      this.subtotalDisplayTarget.textContent = this.displayRateValue ? this.formatApprox(subtotal) : this.formatCurrency(subtotal)
    }
    if (this.hasPlatformFeeDisplayTarget) {
      this.platformFeeDisplayTarget.textContent = this.displayRateValue ? this.formatApprox(platformFee) : this.formatCurrency(platformFee)
    }
    if (this.hasBuyerTotalDisplayTarget) {
      if (this.displayRateValue) {
        this.buyerTotalDisplayTarget.textContent = this.formatApprox(buyerTotal)
        if (this.hasApproxBuyerTotalDisplayTarget) this.approxBuyerTotalDisplayTarget.textContent = "(" + this.formatCurrency(buyerTotal) + ")"
      } else {
        this.buyerTotalDisplayTarget.textContent = this.formatCurrency(buyerTotal)
        if (this.hasApproxBuyerTotalDisplayTarget) this.approxBuyerTotalDisplayTarget.textContent = ""
      }
    }
    if (this.hasSellerReceivesDisplayTarget) {
      this.sellerReceivesDisplayTarget.textContent = this.formatCurrency(subtotal)
    }
  }

  roundTo2(num) {
    return Math.round(num * 100) / 100
  }

  // Mirrors Money#convert_to display rounding: whole baht, nearest 10 above
  // 1,000 baht; other currencies to their decimals. Empty when no rate.
  formatApprox(amount) {
    if (!this.displayRateValue) return ""
    if (!(amount > 0)) return this.displaySymbolValue + (0).toFixed(this.displayDecimalsValue)
    let converted = amount * this.displayRateValue
    let decimals = this.displayDecimalsValue
    if (this.displayCurrencyValue === "THB") {
      converted = converted >= 1000 ? Math.round(converted / 10) * 10 : Math.round(converted)
      decimals = 0
    } else {
      converted = Number(converted.toFixed(decimals))
      if (Number.isInteger(converted)) decimals = 0
    }
    const text = converted.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    return this.displaySymbolValue + text
  }

  formatCurrency(amount) {
    return this.currencySymbolValue + amount.toFixed(2)
  }
}